import { Signal, WritableSignal, computed, signal } from '@angular/core';
import { DeepSignal } from '@ngrx/signals';
import { Observable, take } from 'rxjs';
import { Failure } from './failure';

export type ErrorState = { type: 'error'; failure: Failure; success: false };
export type RequestInput = {
  id?: string;
  data?: unknown;
};

export type SuccessState<T> = {
  data: T;
  type: 'success';
  success: true;
  input?: RequestInput;
};
export type LoadingState = { type: 'loading'; progress: number; success: false };
export type InitialState = { type: 'initial'; success: false };

export type AsyncState<T> = ErrorState | SuccessState<T> | LoadingState | InitialState;
export type Result<T> = SuccessState<T> | ErrorState;

export function stateSignal<T = void>(initial?: AsyncState<T>) {
  return signal<AsyncState<T>>(initial ?? initialState());
}

export function errorState(failure: Failure): ErrorState {
  return { type: 'error', failure, success: false };
}

export function successState<T>(res: T, input?: RequestInput): SuccessState<T> {
  return { type: 'success', data: res, input, success: true };
}

export function patchData<T>(stateSignal: DeepSignal<AsyncState<T>>, updates: Partial<T>) {
  const state = stateSignal();
  if (!state.success) return state;
  return successState({ ...state.data, ...updates });
}

export function loadingState(progress?: number): LoadingState {
  return { type: 'loading', progress: progress ?? 0, success: false };
}

export function initialState(): InitialState {
  return { type: 'initial', success: false };
}

export function computedFailure<T>(state: Signal<AsyncState<T>>) {
  return computed(() => {
    const stateValue = state();
    if (stateValue.type === 'error') {
      return stateValue.failure;
    }
    return undefined;
  });
}

export function computedFailureIf<T>(state: Signal<AsyncState<T>>, condition: Signal<boolean>) {
  return computed(() => {
    if (condition()) return;
    const stateValue = state();
    if (stateValue.type === 'error') {
      return stateValue.failure;
    }
    return undefined;
  });
}

export function computedData<T>(state: WritableSignal<AsyncState<T>>) {
  return computed(() => {
    const stateValue = state();
    if (stateValue.type === 'success') {
      return stateValue.data;
    }
    return undefined;
  });
}

export function asResult<T>(observable: Observable<T>): Promise<Result<T>> {
  return new Promise<Result<T>>((resolve) => {
    observable.pipe(take(1)).subscribe({
      next: (res) => resolve(successState(res)),
      error: (error) => {
        console.error('Error in asResult:', error);
        const failure = Failure.fromHttpError(error);
        return resolve(errorState(failure));
      },
    });
  });
}

export function dataFromState<T, E>(state: DeepSignal<AsyncState<T>>, resolve: (data?: T) => E): Signal<E>;

export function dataFromState<T>(state: DeepSignal<AsyncState<T>>): Signal<T | undefined>;

export function dataFromState<T, E>(state: DeepSignal<AsyncState<T>>, resolve?: (data?: T) => E) {
  return computed(() => {
    const currentState = state();
    const data = currentState.success ? currentState.data : undefined;
    return resolve ? resolve(data) : data;
  });
}
